'use client';

import { useCallback, useMemo, type FC } from 'react';
import { HelperText, Card } from '../../../../utils';
import { useTranslation } from 'next-i18next';
import { cartCouponFormStyles } from './cart-coupon-form.styles';
import type { CartCouponFormProps } from './cart-coupon-form.types';
import { useCartCouponForm } from '../../hooks/use-cart-coupon-form';
import {
    CouponInput,
    useIsMobile,
    withAppBottomSheet,
    type AppBottomSheetInjectedProps,
} from '@kidslabs/mario-nextjs-react-ui-core';
import { CouponScreen } from '../../screens';
import couponFormStyleModule from './cart-coupon-form.module.css';
import { observer } from '@legendapp/state/react';
import { CouponAction } from '../coupon-action/coupon-action';
import { DisplayLocation } from '../../../../types/marketing.graphql';
import { useAddressBook } from '@kidslabs/module-customer';

interface CartCouponFormInnerProps extends CartCouponFormProps, AppBottomSheetInjectedProps {}

type CartCouponFormPublicProps = Omit<CartCouponFormInnerProps, 'bottomSheet'>;

const CartCouponFormInner: FC<CartCouponFormInnerProps> = observer(
    ({ containerClassName, syncStatus, bottomSheet, isCheckoutScreen, shippingDiscountLabel }) => {
        const { t } = useTranslation('flash-sale');
        const isMobile = useIsMobile();
        const couponScreenEnum = isMobile ? 'mini' : 'full';

        const styles = cartCouponFormStyles();

        const { error, appliedCoupon, appliedCoupons, appliedVouchers, refetch, isSelectionEmpty } = useCartCouponForm({
            syncStatus,
        });

        const { loading: addressLoading, filteredAddresses } = useAddressBook({
            initialSelectedShippingAddress: undefined,
            showOnlyDefault: true,
            onAddressSelected: undefined,
            onEditPress: undefined,
            onAddPress: undefined,
            autoSelectDefault: true,
            currentStoreCode: undefined,
        });

        const hasNoAddress = useMemo(() => {
            return addressLoading || !filteredAddresses || filteredAddresses.length === 0;
        }, [addressLoading, filteredAddresses]);

        const effectiveShippingDiscountLabel = useMemo(() => {
            return hasNoAddress ? undefined : shippingDiscountLabel;
        }, [hasNoAddress, shippingDiscountLabel]);

        const onClose = useCallback(() => {
            bottomSheet.close();
            refetch();
        }, [refetch, bottomSheet]);

        const handleNavigateToCouponScreen = useCallback(() => {
            const displayLocation = isCheckoutScreen ? DisplayLocation.CHECKOUT : DisplayLocation.CART;

            bottomSheet.inject(
                <CouponScreen
                    onClose={onClose}
                    variant={couponScreenEnum}
                    displayLocation={displayLocation}
                    isSelectionEmpty={isSelectionEmpty}
                />,
                {
                    title: t('text_select_coupon', 'Chọn mã giảm giá'),
                    titleProps: {
                        variant: 'h2',
                        weight: 'bold',
                        className: couponFormStyleModule.bottomSheetTitle,
                    },
                },
                {
                    scrollEnabled: true,
                    height: 'full',
                }
            );
            bottomSheet.show();
        }, [bottomSheet, onClose, couponScreenEnum, t, isSelectionEmpty, isCheckoutScreen]);

        const renderCouponContent = () => {
            if (appliedCoupon) {
                return (
                    <CouponInput
                        contentClassName={couponFormStyleModule.contentContainer}
                        textInputProps={{ testID: 'cart-coupon-input-text-input' }}
                        textProps={{ testID: 'cart-coupon-input-text' }}
                        onPressCoupon={handleNavigateToCouponScreen}
                        onKeyDownEnter={handleNavigateToCouponScreen}
                        inputPlaceholder={t('text_enter_promo_code')}
                        buttonLabel={t('text_select_code')}
                        couponCode={appliedCoupon}
                        couponText={effectiveShippingDiscountLabel}
                        isInputButton={!appliedCoupon}
                    />
                );
            }

            if (effectiveShippingDiscountLabel) {
                return (
                    <CouponInput
                        contentClassName={couponFormStyleModule.contentContainer}
                        textProps={{ testID: 'cart-coupon-input-text' }}
                        onPressCoupon={handleNavigateToCouponScreen}
                        onKeyDownEnter={handleNavigateToCouponScreen}
                        buttonLabel={t('text_select_code')}
                        inputPlaceholder={t('text_enter_promo_code')}
                    />
                );
            }

            return (
                <div className={couponFormStyleModule.couponActionContainer}>
                    <CouponAction
                        variant="header"
                        onSuccess={refetch}
                        onRefresh={refetch}
                        appliedCoupons={appliedCoupons}
                        appliedVouchers={appliedVouchers}
                        handleNavigateToCouponScreen={handleNavigateToCouponScreen}
                        buttonLabel={t('text_select_code')}
                        inputContainerClassname={couponFormStyleModule.actionInputContainer}
                        containerClassname={couponFormStyleModule.actionContainer}
                        shippingDiscountLabel={effectiveShippingDiscountLabel}
                    />
                </div>
            );
        };

        return (
            <Card mode="elevated" style={styles.container} className={containerClassName}>
                {renderCouponContent()}
                {error && (
                    <HelperText type="error" visible>
                        {error.message}
                    </HelperText>
                )}
            </Card>
        );
    }
);

const WrappedCartCouponForm = withAppBottomSheet(CartCouponFormInner);

const CartCouponForm = WrappedCartCouponForm as FC<CartCouponFormPublicProps>;

export default CartCouponForm;
export { CartCouponForm };
